import React from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import classNames from 'classnames';
import css from './MarkdownText.css';

const MarkdownText = ({ content, className }) => {
    if (!content) return null;

    // Escape double underscores to prevent them from being treated as bold
    const processedContent = content.replace(/__/g, '\\_\\_');

    const customRenderers = {
        // dir="auto" on every text block, not just the wrapper: a reply that mixes a Hebrew
        // paragraph with an English one needs each judged on its own first strong character.
        paragraph: ({ children }) => (
            <p className={css.paragraph} dir="auto">
                {children}
            </p>
        ),

        strong: ({ children }) => <strong className={css.strong}>{children}</strong>,

        emphasis: ({ children }) => <em className={css.emphasis}>{children}</em>,

        list: ({ ordered, children }) => {
            const ListTag = ordered ? 'ol' : 'ul';
            return <ListTag className={css.list}>{children}</ListTag>;
        },

        listItem: ({ children }) => (
            <li className={css.listItem} dir="auto">
                {children}
            </li>
        ),

        // Code stays LTR: a query or a snippet is not prose, and one Hebrew identifier in it
        // would otherwise flip the whole block.
        code: ({ value, children }) => {
            return (
                <pre className={css.codeBlock} dir="ltr">
                    <code>{value || children}</code>
                </pre>
            );
        },

        inlineCode: ({ children }) => (
            <code className={css.inlineCode} dir="ltr">
                {children}
            </code>
        ),

        link: ({ href, children }) => (
            <a href={href} className={css.link} target="_blank" rel="noopener noreferrer">
                {children}
            </a>
        ),

        heading: ({ level, children }) => {
            const HeadingTag = `h${level}`;
            return React.createElement(HeadingTag, { className: css[`heading${level}`], dir: 'auto' }, children);
        },

        blockquote: ({ children }) => (
            <blockquote className={css.blockquote} dir="auto">
                {children}
            </blockquote>
        ),

        // Wide tables must scroll on their own; every chat container clips horizontal overflow.
        table: ({ children }) => (
            <div className={css.tableWrapper}>
                <table>{children}</table>
            </div>
        ),

        // Drop the markdown column alignment on header cells only, so numeric columns keep their
        // right-aligned values while every header stays centered by the stylesheet.
        tableCell: ({ isHeader, align, children }) =>
            isHeader ? (
                <th dir="auto">{children}</th>
            ) : (
                <td dir="auto" style={align ? { textAlign: align } : undefined}>
                    {children}
                </td>
            ),
    };

    return (
        <div className={classNames(css.markdownText, className)} dir="auto">
            <ReactMarkdown source={processedContent} renderers={customRenderers} breaks />
        </div>
    );
};

MarkdownText.propTypes = {
    content: PropTypes.string,
    className: PropTypes.string,
};

export default MarkdownText;
